import HelpIcon from "@mui/icons-material/HelpOutlineOutlined";
import InfoIcon from "@mui/icons-material/Info";
import {
  Box,
  Button,
  Grid,
  IconButton,
  Stack,
  Switch,
  Tooltip,
  Typography
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useCurrency } from "../../context/CurrencyContext";
import { PAYMENT_ITEM_TYPE, PaymentItem } from "../costs/paymentItem.ts";
import { CurrencyTextField } from "../general/CurrencyTextField";
import { DialogShell } from "../general/DialogShell";
import { useTheme } from "../../context/ThemeContext";

export const AfterTheEventInsuranceAgreement = ({
  onPaymentItemsChange,
}: {
  onPaymentItemsChange?: (items: PaymentItem[]) => void;
}) => {
  const { theme } = useTheme();
  const { currency } = useCurrency();
  const { t } = useTranslation(undefined, {
    keyPrefix: "agreement.afterTheEventInsurance",
  });

  const [potentialLoss, setPotentialLoss] = useState(0);
  const [premiumAmount, setPremiumAmount] = useState(0);
  const [partialUpfront, setPartialUpfront] = useState(true);

  // Calculate payments based on the selected option.
  const upfrontPayment = partialUpfront ? premiumAmount * 0.3 : 0;

  // In a loss scenario:
  const amountIfLose = partialUpfront ? upfrontPayment : 0;

  // In a win scenario, the client pays the full premium amount.
  const amountIfWin = premiumAmount;

  const [infoOpen, setInfoOpen] = useState(false);

  // Prepare the payment items for the waterfall
  const paymentItems: PaymentItem[] = useMemo(() => {
    return [
      {
        id: "ate-success",
        name: t("paymentItems.payment"),
        amount: amountIfWin,
        secondaryText: currency.format(amountIfWin),
        paymentType: PAYMENT_ITEM_TYPE.SUCCESS,
      },
      {
        id: "ate-failure",
        name: t("paymentItems.payment"),
        amount: amountIfLose,
        secondaryText: currency.format(amountIfLose),
        paymentType: PAYMENT_ITEM_TYPE.FAILURE,
      },
    ];
  }, [amountIfWin, amountIfLose, currency, t]);

  useEffect(() => {
    if (onPaymentItemsChange) {
      onPaymentItemsChange(paymentItems);
    }
  }, [paymentItems, onPaymentItemsChange]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(2),
      }}
    >
      {/* INSURANCE DETAILS */}
      <Box
        sx={{
          mb: theme.spacing(2),
        }}
      >
        <Typography
          variant="button"
          sx={{
            width: "fit-content",
          }}
        >
          {t("insuranceDetails")}
        </Typography>
        <Box
          sx={{
            p: 2,
            display: "flex",
            border: 1,
            borderColor: theme.palette.divider,
            borderRadius: 2,
            mt: theme.spacing(1),
          }}
        >
          <Grid
            container
            spacing={theme.spacing(2)}
            sx={{ width: "100%", minWidth: "auto" }}
          >
            <Grid size={{ xs: 12, sm: 6 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  mb: theme.spacing(1),
                }}
              >
                <Typography variant="body2">{t("premiumAmount")}</Typography>
                <Tooltip
                  title={t("toolTip.premiumAmount")}
                  arrow
                  placement="right"
                >
                  <IconButton size="small">
                    <InfoIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
              <CurrencyTextField
                value={premiumAmount}
                onValueChange={(val: number) => setPremiumAmount(val)}
                currency={currency}
              />
            </Grid>

            {/* Potential Loss */}
            <Grid size={{ xs: 12, sm: 6 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  mb: theme.spacing(1),
                }}
              >
                <Typography variant="body2">{t("potentialLoss")}</Typography>
                <Tooltip
                  title={t("toolTip.potentialLoss")}
                  arrow
                  placement="right"
                >
                  <IconButton size="small">
                    <InfoIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
              <CurrencyTextField
                value={potentialLoss}
                onValueChange={(val: number) => setPotentialLoss(val)}
                currency={currency}
              />
            </Grid>
          </Grid>

          {/* Payment option selection */}
          <Box>
            <Typography variant="body2" gutterBottom>
              {t("paymentOption")}
            </Typography>
            <Stack
              direction="row"
              spacing={theme.spacing(1)}
              sx={{
                alignItems: "center",
              }}
            >
              <Typography variant="body2">{t("partialUpfront")}</Typography>
              <Switch
                checked={partialUpfront}
                onChange={(e) => setPartialUpfront(e.target.checked)}
                color="primary"
              />
              <Typography variant="body2">{t("fullyDeferred")}</Typography>
            </Stack>
            <Typography variant="caption" color="textSecondary">
              {t("paymentOptionNote")}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Payment obligations */}
      <Box>
        <Box
          sx={{
            display: "flex",
          }}
        >
          <Typography
            variant="button"
            sx={{
              mt: theme.spacing(1),
            }}
          >
            {t("paymentObligation")}
          </Typography>
          <IconButton size="small" onClick={() => setInfoOpen(true)}>
            <HelpIcon fontSize="small" />
          </IconButton>
        </Box>
        <Stack
          spacing={theme.spacing(1)}
          sx={{
            mt: theme.spacing(1),
          }}
        >
          <Typography variant="body2">
            {t("totalDueOnSuccess")}
            {currency.format(amountIfWin)}
          </Typography>
          <Typography variant="body2">
            {t("totalDueOnFailure")}
            {currency.format(amountIfLose)}
          </Typography>
        </Stack>
      </Box>

      {/* Info Dialog for Payment Obligations */}
      <DialogShell
        open={infoOpen}
        onClose={() => setInfoOpen(false)}
        title={t("dialog.title")}
        description={t("dialog.dialogContent")}
        actions={
          <Button
            onClick={() => setInfoOpen(false)}
            variant="contained"
            autoFocus
          >
            {t("dialog.dialogOk")}
          </Button>
        }
      />
    </Box>
  );
};
